/**
 * Summarizes a freshly-imported tournament for the Step 1 review overview: the
 * date span, the game count, and the distinct venues (with resolved addresses).
 * Pure + testable; the UI in ImportOverview.tsx just renders this.
 */

import type { TournamentGameDto } from '../../../../../types/tournament-games';
import type { ScheduleMergeResultResponse } from '../../../../../types/schedule-ingestion';

export interface ImportVenue {
  name: string;
  address: string | null;
}

export interface ImportOverviewSummary {
  gamesCount: number;
  /** e.g. "Jul 20, 2026" for a single day, "Jul 20 – Jul 26, 2026" for a span. Empty when undated. */
  dateRangeLabel: string;
  /** Distinct venues in first-seen order; address is the first resolved one for that name. */
  venues: ImportVenue[];
}

function formatDateInTz(iso: string, timeZone: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  try {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: timeZone || 'UTC' }).format(
      new Date(iso),
    );
  } catch {
    // An invalid IANA id would throw; fall back to the machine's zone rather than lose the label.
    return new Intl.DateTimeFormat('en-US', opts).format(new Date(iso));
  }
}

export function summarizeImport(games: readonly TournamentGameDto[]): ImportOverviewSummary {
  const dated = games.filter((g) => g.startsAtUtc);
  let dateRangeLabel = '';
  if (dated.length > 0) {
    const sorted = [...dated].sort((a, b) => a.startsAtUtc.localeCompare(b.startsAtUtc));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const start = formatDateInTz(first.startsAtUtc, first.timeZone);
    const end = formatDateInTz(last.startsAtUtc, last.timeZone);
    dateRangeLabel = start === end ? start : `${start} – ${end}`;
  }

  const venues: ImportVenue[] = [];
  const seen = new Set<string>();
  for (const g of games) {
    const name = (g.fieldName ?? '').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    venues.push({ name, address: g.fieldAddress });
  }

  return { gamesCount: games.length, dateRangeLabel, venues };
}

/**
 * The owner-facing "what changed" summary of a schedule re-import (merge). Turns a
 * ScheduleMergeResultResponse into display-ready strings for the rebuild summary.
 *
 * Wording notes (see the feature plan):
 * - Added games read as "new games", never as churn. Bracket games that were TBD
 *   and are now set land in the added bucket server-side, so they read the same
 *   plain way here ("N new games") rather than "removed/added".
 * - Conflicts read as "need review" (a locally-edited game the source wanted to
 *   change or drop, kept and flagged for keep-mine / take-theirs).
 * - Removed reads as "removed"; when the mass-delete guard held the removals it is
 *   surfaced separately (see hasHeldMassDelete / heldMassDeleteMessage) rather than
 *   as a plain removed count, since the removals were NOT applied.
 */
export interface MergeSummary {
  /** True when nothing changed at all (a true no-op re-import). */
  isNoOp: boolean;
  /**
   * The change phrases in a stable order, e.g.
   * ["4 new games", "2 changed", "1 needs review", "3 new teams to review"].
   * Empty on a no-op.
   */
  parts: string[];
  /**
   * A single joined headline, e.g. "4 new games, 2 changed, 1 needs review".
   * "No changes from this import." on a no-op.
   */
  headline: string;
  /** True when the mass-delete guard held some removals for confirmation. */
  hasHeldMassDelete: boolean;
  /** Plain-language warning to show when the mass-delete guard tripped; empty otherwise. */
  heldMassDeleteMessage: string;
  /** True when new teams were auto-created and should be reviewed. */
  hasNewTeams: boolean;
  /** e.g. "3 new teams to review"; empty when no new teams. */
  newTeamsLabel: string;
  /** True when there are flagged conflicts to resolve. */
  hasConflicts: boolean;
}

const HELD_MASS_DELETE_MESSAGE =
  'Too many games would be removed, so removals were held. Review before applying.';

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function mergeSummary(result: ScheduleMergeResultResponse): MergeSummary {
  const parts: string[] = [];

  if (result.addedCount > 0) {
    parts.push(countLabel(result.addedCount, 'new game', 'new games'));
  }
  if (result.updatedCount > 0) {
    // "changed" is the plain word for an updated game; count carries the number.
    parts.push(`${result.updatedCount} changed`);
  }
  if (result.conflictCount > 0) {
    // "needs" vs "need" so the copy stays grammatical for any count.
    const verb = result.conflictCount === 1 ? "needs" : "need";
    parts.push(`${result.conflictCount} ${verb} review`);
  }
  // Removals that actually applied. When the guard held them, they did NOT apply,
  // so we surface that separately (heldMassDeleteMessage) instead of counting here.
  if (!result.heldMassDelete && result.removedCount > 0) {
    parts.push(countLabel(result.removedCount, 'removed', 'removed'));
  }

  const hasNewTeams = result.newTeamCount > 0;
  const newTeamsLabel = hasNewTeams
    ? `${countLabel(result.newTeamCount, 'new team', 'new teams')} to review`
    : '';
  if (hasNewTeams) {
    parts.push(newTeamsLabel);
  }

  const isNoOp = parts.length === 0 && !result.heldMassDelete;

  // Headline priority: a no-op says so; otherwise join the change phrases. When the
  // ONLY thing that happened is a held mass-delete (no adds/updates/etc.), parts is
  // empty but it is not a no-op, so give it a headline rather than an empty string.
  let headline: string;
  if (isNoOp) {
    headline = 'No changes from this import.';
  } else if (parts.length === 0) {
    headline = 'Removals were held for your review.';
  } else {
    headline = parts.join(', ');
  }

  return {
    isNoOp,
    parts,
    headline,
    hasHeldMassDelete: result.heldMassDelete,
    heldMassDeleteMessage: result.heldMassDelete ? HELD_MASS_DELETE_MESSAGE : '',
    hasNewTeams,
    newTeamsLabel,
    hasConflicts: result.conflictCount > 0,
  };
}
